import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const MENU_CLOSE_DELAY = 220;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Gives the pointer time to travel from a top-level link into its panel.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivationTimer = null;

  /**
   * Prevent restoring focus after Escape from reopening the menu immediately.
   */
  #restoringFocus = false;

  #hydrationObserver = new MutationObserver(() => {
    // Hydration can reset ARIA attributes on retained nodes or replace them.
    this.#forceDeactivate();
    const hovered = this.querySelector('.menu-list__list-item:hover > .menu-list__link');
    if (hovered) this.activate({ target: hovered });
  });

  connectedCallback() {
    super.connectedCallback();

    const section = this.closest('.header-section');
    if (section) this.#hydrationObserver.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('keydown', this.#documentKeydownListener);
    document.addEventListener('pointerdown', this.#documentPointerDownListener);
    this.addEventListener('pointerenter', this.#cancelDeactivation);
    this.addEventListener('focusin', this.#cancelDeactivation);
    this.addEventListener('click', this.#overflowToggleListener);
    // Delegate to the persistent host: section hydration replaces the More button.
    this.addEventListener('pointerover', this.#overflowHoverListener);
    this.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('keydown', this.#overflowKeyListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#cancelDeactivation);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hydrationObserver.disconnect();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('keydown', this.#documentKeydownListener);
    document.removeEventListener('pointerdown', this.#documentPointerDownListener);
    this.removeEventListener('pointerenter', this.#cancelDeactivation);
    this.removeEventListener('focusin', this.#cancelDeactivation);
    this.removeEventListener('click', this.#overflowToggleListener);
    this.removeEventListener('pointerover', this.#overflowHoverListener);
    this.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('keydown', this.#overflowKeyListener);
    this.overflowMenu?.removeEventListener('pointerenter', this.#cancelDeactivation);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cancelDeactivation();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#forceDeactivate();
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivation();
  };

  #overflowHoverListener = (event) => {
    if (event.pointerType === 'touch' || !(event.target instanceof Element) ||
        !event.target.closest('[slot="more"] > button')) return;
    this.#activateOverflow();
  };

  #overflowKeyListener = (event) => {
    if (event.key !== 'ArrowDown' || !(event.target instanceof Element) ||
        !event.target.closest('[slot="more"] > button')) return;
    event.preventDefault();
    this.#activateOverflow();
    this.querySelector('[slot="overflow"] > a')?.focus();
  };

  #overflowToggleListener = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[slot="more"] > button')) return;
    event.preventDefault();

    if (this.dataset.overflowExpanded === 'true') {
      this.#forceDeactivate(true);
      return;
    }

    this.#activateOverflow();
  };

  #documentKeydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    event.preventDefault();
    this.#forceDeactivate(true);
  };

  #documentPointerDownListener = (event) => {
    if (!this.#state.activeItem || !(event.target instanceof Node) || this.contains(event.target)) return;

    this.#forceDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowTrigger() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[slot="more"] > button'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#restoringFocus) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelDeactivation();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const slottedItem = event.target.closest('.menu-list__list-item');
    const slot = slottedItem?.getAttribute('slot') || '';

    if (slot === 'more') {
      this.#activateOverflow();
      return;
    }

    // Avora renders overflow destinations as a flat directory. Moving through
    // that directory must not transfer "expanded" state to a destination link.
    if (slot === 'overflow' && this.dataset.overflowExpanded === 'true') return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (this.#state.activeItem === item && submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        if (this.#state.activeItem === item) this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #activateOverflow() {
    if (!this.headerComponent || !this.overflowMenu || !this.overflowTrigger) return;
    if (this.#state.activeItem === this.overflowTrigger && this.dataset.overflowExpanded === 'true' &&
        this.overflowTrigger.ariaExpanded === 'true') return;

    this.#cancelDeactivation();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.#cleanupMutationObserver();
    this.#state.activeItem = this.overflowTrigger;
    this.dataset.overflowExpanded = 'true';
    this.ariaExpanded = 'true';
    this.overflowTrigger.ariaExpanded = 'true';
    this.overflowMenu.dataset.active = '';

    const finalHeight = this.#getOverflowListLinksHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    if (event.type === 'pointerleave') {
      this.#scheduleDeactivation();
      return;
    }

    // The browser updates document.activeElement after the blur handler. Waiting
    // one microtask lets the close guard inspect the element that actually
    // received focus instead of the link that just lost it.
    queueMicrotask(() => this.#deactivate());
  }

  /**
   * Delay pointer-driven closing so users can cross small visual gaps without
   * losing the menu. The active item is captured so a stale timer cannot close
   * a different category that opened in the meantime.
   */
  #scheduleDeactivation() {
    const item = this.#state.activeItem;
    if (!item) return;

    this.#cancelDeactivation();
    this.#deactivationTimer = setTimeout(() => {
      this.#deactivationTimer = null;
      this.#deactivate(item);
    }, MENU_CLOSE_DELAY);
  }

  #cancelDeactivation = () => {
    if (this.#deactivationTimer === null) return;

    clearTimeout(this.#deactivationTimer);
    this.#deactivationTimer = null;
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);
    const itemContainer = item.closest('.menu-list__list-item');
    const focusedElement = document.activeElement;
    const pointerIsInside = itemContainer?.matches(':hover') || submenu?.matches(':hover');
    const focusIsInside = focusedElement instanceof Node && itemContainer?.contains(focusedElement);

    // Keep the menu open while the pointer or keyboard focus is anywhere in
    // the active category, including its visually detached submenu.
    if (
      pointerIsInside ||
      focusIsInside ||
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    this.#forceDeactivate();
  };

  /**
   * Close the active menu without hover guards.
   * @param {boolean} [restoreFocus]
   */
  #forceDeactivate(restoreFocus = false) {
    this.#cancelDeactivation();

    const item = this.#state.activeItem;
    if (!item) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
    if (this.overflowMenu) {
      delete this.overflowMenu.dataset.active;
    }

    if (restoreFocus && item.isConnected) {
      this.#restoringFocus = true;
      item.focus();
      this.#restoringFocus = false;
    }
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  const menuItem = listItem?.querySelector(':scope > [ref="menuitem"]');
  return menuItem instanceof HTMLElement ? menuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
